import React, { useState } from "react";
import { Form, Card, Row, Col, Badge, Table } from "react-bootstrap";
import { Order } from "../types";
import ImagePreviewModal from "./shared/ImagePreviewModal";

interface OrderFormProps {
  order: Order;
}

const OrderForm: React.FC<OrderFormProps> = ({ order }) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const getImages = (): string[] => {
    if (!order.formData) return [];
    try {
      const parsed = JSON.parse(order.formData);
      const images: string[] = [];
      for (const key of ["picture1", "picture2", "image"]) {
        if (parsed[key] && typeof parsed[key] === "string" && parsed[key].startsWith("data:image")) {
          images.push(parsed[key]);
        }
      }
      return images;
    } catch {}
    return [];
  };

  const isCelebrationCake = /^celebration\s*cakes?$/i.test(
    order.type?.trim() || ""
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    });
  };

  const getActivityBadge = (activity: string) => {
    switch (activity) {
      case "Created":
        return "success";
      case "Pending":
        return "warning";
      case "Confirmed":
        return "primary";
      default:
        return "secondary";
    }
  };

  return (
    <Card id="printable-order-details">
      <Card.Body>
        <h2>
          View {order.type} Order ({order.code})
        </h2>
        <h5 className="text-muted mt-3">Order Details</h5>
        <hr />

        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Label className="fw-bold">Order Status</Form.Label>
            </Col>
            <Col md={9}>
              <Badge bg="info">{order.status}</Badge>
            </Col>
          </Row>

          {isCelebrationCake && (
            <>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Branch</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control
                    value={order.branch || "Rochdale Branch"}
                    readOnly
                    className="bg-dark text-white"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Collect From</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={
                      order.branch === "Rochdale Branch"
                        ? "123 High Street, Rochdale, OL16 1XN"
                        : "Address not available"
                    }
                    readOnly
                    className="bg-dark text-white"
                  />
                </Col>
              </Row>
            </>
          )}

          <Row className="mb-3">
            <Col md={3}>
              <Form.Label className="fw-bold">Required Date</Form.Label>
            </Col>
            <Col md={3}>
              <Form.Control
                value={
                  order.dateRequired
                    ? new Date(order.dateRequired).toLocaleDateString()
                    : "Not specified"
                }
                readOnly
              />
            </Col>
            <Col md={3}>
              <Form.Label className="fw-bold">Required Time</Form.Label>
            </Col>
            <Col md={3}>
              <Form.Control
                value={
                  order.dateRequired
                    ? new Date(order.dateRequired).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "Not specified"
                }
                readOnly
              />
            </Col>
          </Row>

          {isCelebrationCake ? (
            <>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Size</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.size || ""} readOnly />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Quantity</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.quantity || 1} readOnly />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Shape</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.shape || ""} readOnly />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Cream Type</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control
                    value={order.creamType || ""}
                    readOnly
                    className="bg-dark text-white"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Occasion</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.occasion || ""} readOnly />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Gender</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.gender || ""} readOnly />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Picture Code</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.pictureCode || ""} readOnly />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Picture Name</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.pictureName || ""} readOnly />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="fw-bold">Message on Cake</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control value={order.messageOnCake || "No"} readOnly />
                </Col>
              </Row>
            </>
          ) : (
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label className="fw-bold">Item Details</Form.Label>
              </Col>
              <Col md={9}>
                <Form.Control
                  as="textarea"
                  value="Standard order item details would go here."
                  readOnly
                />
              </Col>
            </Row>
          )}

          {getImages().length > 0 && (
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label className="fw-bold">Pictures</Form.Label>
              </Col>
              <Col md={9}>
                <div className="d-flex gap-3">
                  {getImages().map((img, idx) => (
                    <a
                      key={idx}
                      href="#"
                      className="text-primary text-decoration-none small"
                      onClick={(e) => {
                        e.preventDefault();
                        setPreviewImage(img);
                      }}
                    >
                      <i className="bi bi-image me-1"></i>Picture {idx + 1}
                    </a>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col md={3}>
              <Form.Label className="fw-bold">Any Request or Note</Form.Label>
            </Col>
            <Col md={9}>
              <Form.Control
                as="textarea"
                rows={3}
                value={order.notes || ""}
                readOnly
                className="bg-dark text-white"
              />
            </Col>
          </Row>
        </Form>

        <hr />
        <h5 className="text-muted">Order History</h5>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Activity</th>
                <th>Done By</th>
                <th>Date Time</th>
              </tr>
            </thead>
            <tbody>
              {order.auditLogs && order.auditLogs.length > 0 ? (
                order.auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <Badge bg={getActivityBadge(log.activity)}>
                        {log.activity}
                      </Badge>
                    </td>
                    <td>
                      <i className="bi bi-person me-1"></i>
                      {log.doneBy}
                    </td>
                    <td>
                      <i className="bi bi-calendar me-1"></i>
                      {formatDate(log.date)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">
                    No history available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>

      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />
    </Card>
  );
};

export default OrderForm;
